import { useCallback } from 'react';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import {
  AdMob,
  AdmobConsentStatus,
  RewardAdPluginEvents,
} from '@capacitor-community/admob';

import { ENV } from '@/common/app/env';
import {
  type AdsCallbackArgsType,
  type UseAdsHookReturnType,
} from '@/common/types/ads';

// Google's public test units — used until the app's own AdMob units are set
// in .env.capacitor (VITE_ADMOB_REWARDED_IOS / _ANDROID)
const TEST_REWARDED = {
  ios: 'ca-app-pub-3940256099942544/1712485313',
  android: 'ca-app-pub-3940256099942544/5224354917',
};

const rewardedUnit = (): string =>
  Capacitor.getPlatform() === 'android'
    ? ENV.ADMOB_REWARDED_ANDROID || TEST_REWARDED.android
    : ENV.ADMOB_REWARDED_IOS || TEST_REWARDED.ios;

let ready: Promise<void> | null = null;

/**
 * Once per launch: SDK start, the iOS tracking prompt (ATT) and Google's
 * GDPR consent form where the law requires it. Ads are shown either way —
 * non-personalized when the player declines.
 */
const prepare = (): Promise<void> => {
  ready ??= (async (): Promise<void> => {
    await AdMob.initialize();

    try {
      const { status } = await AdMob.trackingAuthorizationStatus();

      if (status === 'notDetermined') {
        await AdMob.requestTrackingAuthorization();
      }
    } catch {
      // Android: there is no tracking prompt
    }

    try {
      const consent = await AdMob.requestConsentInfo();

      if (
        consent.isConsentFormAvailable &&
        consent.status === AdmobConsentStatus.REQUIRED
      ) {
        await AdMob.showConsentForm();
      }
    } catch {
      // consent service unreachable: the SDK falls back to limited ads
    }
  })().catch((error) => {
    ready = null;
    throw error;
  });

  return ready;
};

/**
 * Rewarded video for the App Store / Play build. The reward is granted only
 * when AdMob reports it earned; closing the video early gives nothing and,
 * like a skipped Adsgram ad, shows no error.
 */
const useAdmobAds = (): UseAdsHookReturnType =>
  useCallback(async ({ onReward }: AdsCallbackArgsType) => {
    await prepare();
    await AdMob.prepareRewardVideoAd({ adId: rewardedUnit() });

    let earned = false;
    const listeners: PluginListenerHandle[] = [];

    try {
      const dismissed = new Promise<void>((resolve, reject) => {
        void Promise.all([
          AdMob.addListener(RewardAdPluginEvents.Rewarded, () => {
            earned = true;
          }),
          AdMob.addListener(RewardAdPluginEvents.Dismissed, () => resolve()),
          AdMob.addListener(RewardAdPluginEvents.FailedToShow, (error) =>
            reject(new Error(error?.message ?? 'AdMob failed to show')),
          ),
        ]).then((handles) => listeners.push(...handles));
      });

      await AdMob.showRewardVideoAd();
      await dismissed;
    } finally {
      listeners.forEach((listener) => void listener.remove());
    }

    if (earned) onReward?.();
  }, []);

export { useAdmobAds };
